import PublicFormView from "@/pages/forms/PublicFormView";
/**
 * HustleMeetCirclesSalesPage.jsx — /circles
 *
 * Dedicated sales page for the "Circles" sub-product: host-led recurring
 * supper clubs. Fully public — no login required. Every string is
 * translated via `hm(t, "circles.<key>")`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, Utensils, CalendarClock, MapPin, ArrowRight, Sparkles,
  Shield, Heart, Star, ChefHat, RefreshCw, CheckCircle2, Mail, Loader2,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { hm } from "@/lib/hmCopy";

/* ── Long-form sales content (English-only for now, matches
 *  HustleMeetups overview page pattern — will move into hmCopy.js
 *  once translation batch runs). Kept outside the component so the
 *  arrays aren't rebuilt on every render. */
const CIRCLES_HERO_IMG = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80";
const CIRCLES_IMG_CREW = "https://images.unsplash.com/photo-1529543544282-ea669407fca3?auto=format&fit=crop&w=1600&q=80";
const CIRCLES_IMG_CHEF = "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=1600&q=80";
const CIRCLES_IMG_REG  = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=80";

const CIRCLES_INTRO_PARAS = [
  "Circles is the modern successor to meet-up culture — with one meaningful difference. Instead of meeting in a park or a random pub, every Circle is anchored to a real venue partnership. Restaurants, cafés, coffee shops, private clubs, wine bars, cocktail lounges, cruise lines and travel agencies have signed up to host the groups that meet on HustleMeetups. Which means when you find a Circle you like, there's already a warm seat with your name on it waiting somewhere real.",
  "The other difference is that Circles is designed for the friends, colleagues and acquaintances who actually want to socialise together, not for strangers being thrown into the deep end. Every Circle is visible to browse. You can see what it's about, how big it is, roughly where it meets and what the crew looks like. But the fine details — venue address, exact dates, phone contacts, the group chat — stay private until the group admin accepts your request to join. Circles that want to be intimate stay intimate. Circles that want to grow can grow.",
  "You don't have to be the organiser of anything. You just have to find the Circle that fits your life and ask to join.",
];

const CIRCLES_SECTIONS = [
  {
    title: "Real Groups. Real Venues. Real People.",
    paragraphs: [
      "Meet-up culture broke because it treated every group the same and every venue as an afterthought. Someone would suggest a park at 2pm. Someone else would counter with a coffee shop. Twenty people would say yes and six would show up. The whole thing depended on one enthusiastic person doing all the coordinating and never getting a return on their effort.",
      "Circles rebuilds the meet-up model around a simple insight: if the venue is already committed, the group runs itself. Every Circle on HustleMeetups is paired with real venue partners who've agreed to host the group. A Sunday-morning walking Circle finishes at a partnered café that opens early for them. A Wine Wednesday Circle rotates through a curated list of partnered wine bars that hold a specific table each week. A cocktail networking Circle meets at a partnered rooftop bar that comps the first round. The venue is doing half the work before the invitation even goes out.",
    ],
  },
  {
    title: "Built Around Where You Actually Want to Be",
    paragraphs: [
      "Most of us don't want to meet in a park. We want to meet somewhere we'd already be. A café we've been meaning to try. A restaurant with a good wine list. A cocktail bar with a small-plates menu. A gastropub with a decent Sunday roast. A rooftop terrace on a Friday. A cruise ship in July. A yoga retreat in Bali.",
      "Circles brings all of these under one roof. Every Circle picks a lane — food, drinks, live music, cruise travel, retreats, cafés — and every Circle is anchored to real venue partnerships that suit that lane. When you browse Circles by suburb or by interest, you're not browsing hopeful ideas. You're browsing groups that are already meeting somewhere real, on a regular rhythm, with venues that want them there.",
    ],
  },
  {
    title: "How Circles Work: Browse, Apply, Get Accepted, Show Up",
    paragraphs: [
      "The flow is deliberately simple. Every verified HustleMeetups member can browse every Circle on the platform. You see the Circle's name, its tagline, its cadence, its rough location (suburb, not address), its interest tags, and a snapshot of its crew — how big it is, how long it's been running, whether it's mostly professionals or mostly social, mostly members in their twenties or a broader mix.",
      "When a Circle looks like a fit, tap \u201Capply to join\u201D. Some Circles are open and accept you instantly. Others are curated — the group admin reviews your profile, might ask a short question, and approves within a day or two. Once you're in, the full details unlock: the exact venue for the next event, the group chat, the RSVP page, the seat count. From there, you just show up when a date suits you.",
    ],
  },
  {
    title: "Why the \u201CApply to Join\u201D Model Changes Everything",
    paragraphs: [
      "Open discovery + gated joining is the single design decision that makes Circles work where regular meet-ups don't. Discovery is open because groups need to be findable — a Circle that nobody can see will never grow. Joining is gated because groups need to protect their culture — a Circle whose venue address is public will get flooded by walk-ins and stop feeling like a group at all.",
      "For members, this means you never turn up to a dinner and find twenty strangers you weren't expecting. For group admins, this means you get to shape the crew as it grows. For venue partners, this means the group that walks through the door is the group they were expecting to host. The membership fits the venue. The venue fits the group. Everyone knows what they signed up for.",
    ],
  },
  {
    title: "Our Venue Partners: Restaurants, Cafés, Clubs, Pubs and Beyond",
    paragraphs: [
      "Every Circle is paired with real venue partners who've agreed to host that specific group. Restaurants — from neighbourhood bistros to fine-dining tasting menus. Cafés — from early-morning walking-club coffee stops to Saturday brunch collectives. Coffee shops — for founders' groups, remote-work crews, first-Monday-of-the-month networking. Wine bars and cocktail lounges — for after-work groups that want a curated wine flight or signature cocktails on arrival.",
      "Pubs and gastropubs — for Sunday roasts, quiz nights, live-music Circles. Private clubs and members' lounges — for professional Circles that want a quieter room and a longer sitting. Sports venues — for game-night groups that book a private box. Live-music and comedy venues — for regular Circles that lock in front-row seats every month. Every partner offers something specific to the Circles it hosts — a held table, a set menu, a comped first round, an early-open, a private room, priority booking, a group discount. The offer is baked into the Circle from the moment you're accepted.",
    ],
  },
  {
    title: "Cruise Ships and Travel Agencies: Circles That Actually Travel",
    paragraphs: [
      "Some Circles don't just meet at restaurants. They travel together. HustleMeetups has partnered with cruise lines and travel agencies to run Circles that plan and take an annual trip together — a Pacific cruise in July, a Bali wellness retreat in September, a Broome road trip in October, a Queenstown ski week in June.",
      "These Circles work exactly the same way as venue-based Circles. You apply, you're accepted, you see the full itinerary, and you decide which trips to join. Travel Circles usually run a monthly get-together at a partnered venue in between trips — a wine night, a planning dinner, a curry group — so the crew stays warm year-round. Some members join a travel Circle purely for the annual trip. Others join for the monthly meets and go along on the trip when it suits. Either works.",
    ],
  },
  {
    title: "For Members: Find the Circles That Fit Your Life",
    paragraphs: [
      "You can join as many Circles as you want. Most active members are in three to six. A dinner Circle in your suburb. A cocktail Circle in the city. A Sunday walking group. A once-a-quarter cruise Circle. A book-and-wine group. Some meet weekly. Others meet monthly. Others meet only when there's something worth turning up to.",
      "Because every Circle discloses its cadence, venue-partner list and rough location before you apply, you never accidentally sign up to something that won't fit your calendar. Your dashboard shows the next event across every Circle you're in, in one place. RSVP with a tap. Add to your phone calendar with another. That's the entire ongoing commitment.",
    ],
  },
  {
    title: "For Group Admins: Run a Circle Without the Admin",
    paragraphs: [
      "Circle admins are people who love bringing others together. Some are founders building a professional community around their industry. Some are food-lovers who want a regular dinner club. Some are travel enthusiasts building a trips-and-adventures crew. Some are just really social people.",
      "What Circle admins don't have to do is admin. Every Circle admin gets a dashboard that handles the boring parts: venue partner rotation, table bookings, RSVP tracking, member vetting, event reminders, wet-weather backups, dietary requirement pass-throughs. Applications from prospective members arrive as a queue with a one-tap approve/decline flow. Payments (where the Circle charges a per-event fee to cover venue costs) route through HustleSuite's ledger. The admin's job is to shape the group and pick the events. HustleMeetups does everything else.",
    ],
  },
  {
    title: "For Venues: Turn a Slow Tuesday Into a Standing Booking",
    paragraphs: [
      "Every venue partner on Circles gets standing bookings that fill their slowest nights. A restaurant that partners with two Circles for Tuesday and Wednesday evenings might pick up an extra thirty guaranteed covers a week — with no marketing spend, no chasing, and no cancellations at short notice.",
      "Venues get a partner dashboard showing which Circles are booked for the coming month, how many guests are RSVP'd, any dietary requirements, and a direct line to each Circle's admin. Partnership terms vary — some venues offer a discount, some hold a specific table, some run a private set menu, some open early or stay open late for the group. HustleMeetups handles the introductions, the booking flow and the reminders. The venue just runs the room.",
    ],
  },
  {
    title: "Verified Members. Trusted Groups.",
    paragraphs: [
      "Every HustleMeetups member is verified via a real ID check and carries a reliability score based on how consistently they RSVP and turn up. Every group admin can see this score at application time. Members who no-show repeatedly are eventually declined. Members who show up warmly and reliably build a reputation that opens doors to more selective Circles.",
      "Circles is a friendship-first platform. There is no dating overlay. The community expects Circles to feel welcoming, curious and reliable — and the moderation model ensures it. One report, one review, one decision. Bad actors are removed the same night.",
    ],
  },
  {
    title: "Circles vs Regular Meet-ups: What Makes This Different",
    paragraphs: [
      "Regular meet-up platforms treat every group as a public event. Everyone shows up. The organiser burns out. The venue never sees a return. Circles inverts every one of those defaults.",
      "Groups are discoverable but joining is gated — so the culture holds. Venues are partners, not afterthoughts — so the group has a real seat at a real table. Group admins get software instead of spreadsheets — so nobody burns out doing coordination. Members carry verified profiles with reliability scores — so RSVPs actually mean something. Every event is anchored to a real business with a real interest in making the group feel welcome. Everything about Circles is designed to make people come back.",
    ],
  },
  {
    title: "Getting Started: Join a Circle in Ten Minutes",
    paragraphs: [
      "Signing up is straightforward. Verify your HustleMeetups profile (one photo ID, ninety seconds). Browse Circles by suburb or interest — filters cover food style, cadence, group size, venue type, whether the Circle is professional-lean or social-lean, whether it travels, whether it's evening-only or morning-friendly. Tap \u201Capply to join\u201D on anything that fits. Answer the short application question if the Circle has one.",
      "Approvals typically arrive within a day. Once you're in, the group chat unlocks, the venue and date for the next event appear on your dashboard, and you RSVP with a single tap. Show up. Meet the crew. Come back next time. That's the whole model.",
    ],
  },
];


const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function WaitlistForm({ t }) {
  const { i18n } = useTranslation();
  const success = hm(t, "circles.waitlist_success");

  return (
    <section
      data-testid="circles-waitlist"
      className="rounded-3xl border border-amber-500/40 bg-amber-500/5 dark:bg-amber-500/10 p-8 md:p-10 mb-8"
    >
      <div className="grid md:grid-cols-[1fr_1.4fr] gap-6 items-center">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.28em] text-amber-700 dark:text-amber-300">
            {hm(t, "circles.waitlist_kicker")}
          </p>
          <h3 className="mt-2 text-2xl md:text-3xl font-black text-stone-900 dark:text-stone-50">
            {hm(t, "circles.waitlist_title")}
          </h3>
          <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
            {hm(t, "circles.waitlist_sub")}
          </p>
        </div>
        <div className="rounded-2xl bg-white p-5 text-slate-900" data-testid="waitlist-form">
          <PublicFormView inline formSlug="circles-postcode-waitlist" sourceUrl={window.location.href}
            locale={i18n.resolvedLanguage || i18n.language || "en"}
            fieldLabels={{ postcode: hm(t, "circles.waitlist_postcode_ph"), name: hm(t, "circles.waitlist_name_ph") }}
            submitLabel={hm(t, "circles.waitlist_cta")} thankyouMessage={success} />
        </div>
      </div>
    </section>
  );
}

function LiveCircleCard({ c, hostPill }) {
  return (
    <Link to={`/circles/${c.slug || c.id}`}
      data-testid={`live-circle-${c.id}`}
      className="group rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 hover:border-amber-400 transition"
    >
      <div className="h-32 bg-gradient-to-br from-amber-500 to-orange-600 relative"
        style={c.cover_image ? {
          backgroundImage: `url(${c.cover_image})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : undefined}
      >
        <span className="absolute top-3 left-3 bg-white/90 dark:bg-slate-950/80 text-amber-500 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
          <Users size={11} aria-label="Your crew" title="Your crew"/> {hostPill}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-black text-base group-hover:text-amber-500 transition line-clamp-2">
          {c.name}
        </h3>
        {c.tagline && <p className="mt-1 text-xs text-stone-700 dark:text-stone-300 line-clamp-2">{c.tagline}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-stone-700 dark:text-stone-300">
          <span className="inline-flex items-center gap-1">
            <MapPin size={11} aria-label="Location" title="Location"/> {c.venue_address || c.vendor_name}
          </span>
          {c.cadence && (
            <span className="inline-flex items-center gap-1">
              <CalendarClock size={11}/> {c.cadence}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function HustleMeetCirclesSalesPage() {
  const { t, i18n } = useTranslation();
  const [circles, setCircles] = useState(null);
  const [venueCount, setVenueCount] = useState(3);
  const [partnerStats, setPartnerStats] = useState({
    partner_venue_count: null, cities: null,
  });

  const HM_NAV = [
    { to: "/meet",             label: hm(t, "nav.hustlemeet") },
    { to: "/circles",     label: hm(t, "nav.circles") },
    { to: "/matchmaker",  label: hm(t, "nav.matchmaker") },
    { to: "/verified-members",       label: hm(t, "nav.verified") },
  ];

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = hm(t, "circles.page_title");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hustlemeet-circles/catalog`);
        setCircles(data?.circles || []);
      } catch { setCircles([]); }
      // Pull the dynamic venue-shortlist size from Agency Settings so
      // this page's copy reflects whatever the operator has configured.
      try {
        const { data } = await axios.get(`${API}/hustlemeet-settings`);
        const n = parseInt(data?.venue_shortlist_size, 10);
        if (Number.isFinite(n) && n >= 2 && n <= 8) setVenueCount(n);
      } catch { /* leave default */ }
      // Q5a — live partner-venue count for the truthfulness proof strip.
      try {
        const { data } = await axios.get(`${API}/hustlemeet-venue-partners/public/stats`);
        setPartnerStats(data || {});
      } catch { /* keep null */ }
    })();
  }, [t, i18n.language]);

  const heroTitle = hm(t, "circles.hero_title");
  const heroAccent = hm(t, "circles.hero_title_accent");
  const heroParts = heroTitle.split(heroAccent);

  return (
    <div
      data-testid="hustlemeet-circles-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: hm(t, "nav.hustlemeet"), to: "/meet" }}
        nav={HM_NAV}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section data-testid="circles-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={{ background: "linear-gradient(135deg,#0a0908 0%,#1c1917 55%,#7c2d12 120%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "circles.kicker")}
          </p>
          <h1 className="mt-3 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl">
            {heroParts.length === 2 ? (
              <>{heroParts[0]}<span className="text-amber-400">{heroAccent}</span>{heroParts[1]}</>
            ) : heroTitle}
          </h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl">{hm(t, "circles.hero_sub")}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild
              className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black h-12 px-6"
              data-testid="cta-join-membership"
            >
              <a href="https://app.hustlemeetups.au/signup" rel="noopener">
                {hm(t, "circles.cta_join") || "Join Hustle Membership"}
              </a>
            </Button>
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-find-circle"
            >
              <a href="#live-circles">{hm(t, "circles.cta_find")}</a>
            </Button>
            <Button asChild variant="outline"
              className="rounded-full border-amber-400 text-amber-300 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="cta-become-host-hero"
            >
              <Link to="/meet/host/apply">{hm(t, "circles.cta_host_hero")}</Link>
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl text-white/70 text-xs">
            {[
              [Utensils, "circles.trust_venue"],
              [Users,    "circles.trust_party"],
              [Shield,   "circles.trust_verified"],
              [Heart,    "circles.trust_friendship"],
            ].map(([I, k], i) => (
              <div key={i} className="inline-flex items-center gap-2">
                <I size={14} className="text-amber-400"/> {hm(t, k)}
              </div>
            ))}
          </div>

          {/* Q5a — Live partner proof */}
          <div
            data-testid="circles-partner-proof"
            className="mt-6 inline-flex items-center gap-3 text-[13px] font-semibold text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-full px-4 py-2"
          >
            <Utensils size={14} className="text-amber-300"/>
            <span>
              <strong className="text-white">{partnerStats.partner_venue_count ?? "—"}</strong>{" "}
              partnered {partnerStats.partner_venue_count === 1 ? "venue" : "venues"}
              {partnerStats.cities ? ` in ${partnerStats.cities} ${partnerStats.cities === 1 ? "city" : "cities"}` : ""} — and growing.
            </span>
            <Link to="/venue-partners/apply" className="ml-2 underline text-amber-100">
              Add yours →
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">

        {/* How Circles work */}
        <section data-testid="how-circles-work">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "circles.how_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "circles.how_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[[Users, 1], [Utensils, 2], [Heart, 3], [RefreshCw, 4]].map(([Ico, n]) => (
              <div key={n} data-testid={`circle-step-${n}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                  {hm(t, `landing.how_step_num`, { defaultValue: `Step ${n}` }).replace(/\{n\}/, n)}
                </p>
                <p className="mt-1 font-black">{hm(t, `circles.how_step${n}_ttl`)}</p>
                <p className="mt-1 text-xs text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `circles.how_step${n}_sub`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Cadence */}
        <section data-testid="cadence-section" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["weekly", "fortnight", "monthly"].map((key) => (
            <div key={key} data-testid={`cadence-${key}`}
              className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                <CalendarClock size={18}/>
              </div>
              <p className="mt-4 font-black">{hm(t, `circles.cadence_${key}_ttl`)}</p>
              <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                {hm(t, `circles.cadence_${key}_sub`)}
              </p>
              <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                {hm(t, `circles.cadence_${key}_body`)}
              </p>
            </div>
          ))}
        </section>

        {/* Feast modes */}
        <section data-testid="feast-modes">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "circles.feast_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "circles.feast_title")}</h2>
          <p className="mt-3 text-sm text-stone-700 dark:text-stone-300 max-w-2xl">{hm(t, "circles.feast_sub")}</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[[ChefHat, "surprise"], [Utensils, "preselect"], [Star, "per_event"]].map(([Ico, k]) => (
              <div key={k} data-testid={`feast-${k}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 font-black">{hm(t, `circles.feast_${k}_ttl`)}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `circles.feast_${k}_body`, k === "surprise" ? { count: venueCount } : {})}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Long-form sales article (English source-of-truth). */}
        <article
          data-testid="circles-sales-article"
          className="border-t border-stone-200 dark:border-stone-800 pt-12"
        >
          <figure
            data-testid="circles-hero-figure"
            className="rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm mb-12"
          >
            <img
              src={CIRCLES_HERO_IMG}
              alt="A long candle-lit dinner table with wine glasses being raised — a Circle of regulars mid-toast"
              loading="lazy"
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
            />
            <figcaption className="text-[11px] text-stone-500 dark:text-stone-400 px-4 py-3 bg-stone-50 dark:bg-stone-900/50">
              A Circle mid-toast — same crew, new table, no one had to organise it.
            </figcaption>
          </figure>

          <p
            className="text-2xl sm:text-3xl font-black leading-snug text-stone-900 dark:text-stone-50 max-w-3xl"
            data-testid="circles-sales-lead"
          >
            Your Crew. A New Table. <span className="text-amber-500">Every Time.</span>
          </p>
          <div className="max-w-3xl">
            {CIRCLES_INTRO_PARAS.map((para, i) => (
              <p
                key={`c-intro-${i}`}
                className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
              >
                {para}
              </p>
            ))}
          </div>

          {CIRCLES_SECTIONS.map((s, i) => (
            <section
              key={`c-sec-${i}`}
              data-testid={`circles-sales-section-${i}`}
              className="mt-12 max-w-3xl"
            >
              <h2 className="text-2xl sm:text-3xl font-black leading-tight text-stone-900 dark:text-stone-50">
                {s.title}
              </h2>
              {s.paragraphs.map((para, pi) => (
                <p
                  key={`c-p-${pi}`}
                  className="mt-4 text-sm sm:text-base text-stone-700 dark:text-stone-200 leading-relaxed"
                >
                  {para}
                </p>
              ))}
              {/* Sprinkle two more images through the article */}
              {i === 2 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={CIRCLES_IMG_CREW}
                    alt="A group of friends laughing at a restaurant table — the moment a Circle's rhythm kicks in"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
              {i === 5 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={CIRCLES_IMG_REG}
                    alt="A warmly lit restaurant interior — regulars welcomed like family"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
              {i === 9 && (
                <figure className="mt-8 rounded-3xl overflow-hidden border border-stone-200 dark:border-stone-800 shadow-sm">
                  <img
                    src={CIRCLES_IMG_CHEF}
                    alt="A chef plating a course for a Circle's Chef's Table dinner"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                </figure>
              )}
            </section>
          ))}

          <div
            data-testid="circles-sales-signature"
            className="mt-14 pt-8 border-t border-stone-200 dark:border-stone-800 max-w-3xl"
          >
            <p className="text-lg sm:text-xl font-black text-stone-900 dark:text-stone-50">
              Circles
            </p>
            <p className="mt-1 text-lg sm:text-xl font-bold text-stone-800 dark:text-stone-100">
              Your Crew. Your Cadence.{" "}
              <span className="text-amber-500">Your Standing Table.</span>
            </p>
          </div>
        </article>

        {/* Live circles catalog */}
        <section id="live-circles" data-testid="live-circles-catalog">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
                {hm(t, "circles.live_kicker")}
              </p>
              <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "circles.live_title")}</h2>
            </div>
            <Link to="/meet/host/apply"
              className="text-sm font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
              data-testid="cta-host-your-own"
            >
              {hm(t, "circles.live_host_link")} <ArrowRight size={14} aria-hidden="true"/>
            </Link>
          </div>
          {circles === null && (
            <p className="text-stone-600 dark:text-stone-300" data-testid="live-circles-loading">{hm(t, "circles.live_loading")}</p>
          )}
          {circles && circles.length === 0 && (
            <div data-testid="live-circles-empty"
              className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-10 text-center"
            >
              <Users size={28} className="mx-auto text-amber-500" aria-label="Your crew" title="Your crew"/>
              <p className="mt-4 font-semibold">{hm(t, "circles.live_empty_title")}</p>
              <p className="mt-2 text-xs text-stone-700 dark:text-stone-300">{hm(t, "circles.live_empty_sub")}</p>
              <Button asChild
                className="mt-5 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                data-testid="cta-join-waitlist"
              >
                <Link to="/signup">{hm(t, "circles.live_empty_cta")}</Link>
              </Button>
            </div>
          )}
          {circles && circles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {circles.map((c) => (
                <LiveCircleCard key={c.id} c={c} hostPill={hm(t, "circles.card_host_pill")}/>
              ))}
            </div>
          )}
        </section>

        {/* Become a host */}
        <section data-testid="become-host-section"
          className="rounded-3xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-8 md:p-12"
        >
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "circles.host_kicker")}
          </p>
          <h2 className="mt-2 text-2xl md:text-4xl font-black">{hm(t, "circles.host_title")}</h2>
          <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-2xl">{hm(t, "circles.host_body")}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li key={n} className="inline-flex items-start gap-2 text-sm">
                <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" aria-label="Included" title="Included"/>
                <span>{hm(t, `circles.host_perk_${n}`)}</span>
              </li>
            ))}
          </ul>
          <Button asChild
            className="mt-7 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
            data-testid="cta-become-host-cta"
          >
            <Link to="/meet/host/apply">{hm(t, "circles.host_cta")}</Link>
          </Button>
        </section>

        {/* Why Circles work */}
        <section data-testid="why-circles-work">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            {hm(t, "circles.why_kicker")}
          </p>
          <h2 className="mt-1 text-2xl md:text-4xl font-black">{hm(t, "circles.why_title")}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[[Users, 1], [Shield, 2], [Heart, 3]].map(([Ico, n]) => (
              <div key={n}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Ico size={18}/>
                </div>
                <p className="mt-4 font-black">{hm(t, `circles.why_${n}_ttl`)}</p>
                <p className="mt-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                  {hm(t, `circles.why_${n}_body`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Postcode waitlist */}
        <WaitlistForm t={t} />

        {/* Bottom CTA */}
        <section data-testid="circles-bottom-cta"
          className="text-center rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-8 md:p-12"
        >
          <Sparkles size={28} className="mx-auto text-amber-500" aria-label="AI Matchmaker" title="AI Matchmaker"/>
          <h2 className="mt-4 text-2xl md:text-4xl font-black">{hm(t, "circles.bottom_title")}</h2>
          <p className="mt-3 text-sm md:text-base text-stone-700 dark:text-stone-300 max-w-2xl mx-auto">
            {hm(t, "circles.bottom_sub")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild
              className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-12 px-6"
              data-testid="cta-bottom-find-circle"
            >
              <a href="#live-circles">{hm(t, "circles.bottom_cta_find")}</a>
            </Button>
            <Button asChild variant="outline"
              className="rounded-full border-amber-400 text-amber-500 hover:bg-amber-500/10 font-black h-12 px-6"
              data-testid="cta-bottom-matchmaker"
            >
              <Link to="/matchmaker">{hm(t, "circles.bottom_cta_mm")}</Link>
            </Button>
          </div>
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
